import React, { useState, useEffect, useCallback } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import { extractDomainIdentifier, cleanCampaignName, extractAccountBadge } from './utils/campaignGrouping';

/**
 * Component for a single campaign table row
 */
const CampaignTableRow = ({
  campaign,
  index,
  isSubid3View,
  getTaboolaCampaignData,
  isTaboolaCampaign,
  getFacebookCampaignData,
  isFacebookCampaign,
  handleDrillDown,
  openBudgetDialog,
  toggleCampaignStatus,
  handleBlacklist,
  loading,
  isBlacklisted,
  selectedSubid2,
  facebookCampaigns,
  channelWarnings,
  isChildRow = false
}) => {
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState({});
  
  // Function to copy campaign ID to clipboard
  const handleCopyId = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      await navigator.clipboard.writeText(campaign.campaign_id);
      // You could add a toast notification here if available
      console.log('Campaign ID copied to clipboard:', campaign.campaign_id);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = campaign.campaign_id;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      console.log('Campaign ID copied to clipboard (fallback):', campaign.campaign_id);
    }
  }, [campaign.campaign_id]);
  
  // Memoize the isFacebookCampaign check to prevent it from causing re-renders
  const checkIsFacebookCampaign = useCallback(() => {
    return campaign.platform === 'facebook' || 
      (typeof isFacebookCampaign === 'function' && isFacebookCampaign(campaign.campaign_id));
  }, [campaign.campaign_id, campaign.platform, isFacebookCampaign]);
  
  // Memoize the getFacebookCampaignData call to prevent it from causing re-renders
  const getFbCampaignData = useCallback(() => {
    return getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
  }, [campaign.campaign_id, getFacebookCampaignData, facebookCampaigns]);


  // Handle blacklisting a subid3
  const onBlacklist = async (e) => {
    // Stop event propagation to prevent row click
    e.stopPropagation();
    e.preventDefault();
    
    // Set local loading state
    setBlacklistLoading(true);
    
    // Add more detailed logging 
    console.log('Blacklist action initiated for:', {
      campaign_id: campaign.campaign_id,
      subid2: campaign.subid2,
      selectedSubid2: selectedSubid2,
      isSubid3View: isSubid3View,
      campaign_data: campaign
    });

    try {
      if (handleBlacklist) {
        // In subid3 view, use the selectedSubid2 (parent campaign ID) as the primary source
        // Otherwise, try to get it from the campaign object
        const subid2 = isSubid3View 
          ? (selectedSubid2 || campaign.subid2 || '')
          : (campaign.subid2 || selectedSubid2 || '');
        
        // Final log before making the call
        console.log('Calling handleBlacklist with:', {
          subid3: campaign.campaign_id,
          subid2: subid2
        });
        
        // Exit early if we don't have a valid subid2
        if (!subid2) {
          console.error('Cannot blacklist: missing subid2/campaign ID');
          return;
        }
        
        // Call the blacklist handler and wait for it to complete
        await handleBlacklist(campaign.campaign_id, subid2);
      } else {
        console.error('handleBlacklist function is not available');
      }
    } catch (error) {
      console.error('Error in blacklist operation:', error);
    } finally {
      // Reset loading state when done
      setBlacklistLoading(false);
    }
    
    // Prevent default to ensure no navigation/refresh happens
    return false;
  };

  // Check if this site is already blacklisted
  const siteBlacklisted = isBlacklisted && isBlacklisted(campaign.campaign_id);

  // Add debug logging for specific campaign IDs
  if (campaign.campaign_id === '120225374662480057' || campaign.campaign_id === '120236832581510276') {
    const fbData = getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
    console.log(`🔍 DEBUG Campaign ${campaign.campaign_id}:`, {
      campaign_name: campaign.campaign_name,
      platform: campaign.platform,
      isFacebookCampaign: isFacebookCampaign(campaign.campaign_id),
      facebookCampaignsCount: facebookCampaigns ? Object.keys(facebookCampaigns).length : 0,
      facebookCampaignIds: facebookCampaigns ? Object.keys(facebookCampaigns).slice(0, 10) : [],
      foundInFacebookCampaigns: facebookCampaigns ? !!facebookCampaigns[campaign.campaign_id] : false,
      getFacebookCampaignDataResult: fbData,
      hasBudget: fbData ? (fbData.daily_budget || fbData.lifetime_budget) : null,
      isActive: fbData ? fbData.is_active : null
    });
  }

  return (
    <TableRow
      sx={{
        backgroundColor: isChildRow
          ? 'rgba(25, 118, 210, 0.03)'
          : (index % 2 === 0 ? 'background.default' : 'background.paper'),
        '&:hover': {
          backgroundColor: 'action.hover',
          boxShadow: 1
        },
        transition: 'background-color 0.2s',
        ...(isChildRow && {
          '& > td:first-of-type': {
            borderLeft: '3px solid',
            borderLeftColor: 'primary.light',
            paddingLeft: '12px'
          }
        })
      }}
      data-campaign-id={campaign.campaign_id}
      data-platform={campaign.platform}
    >
      <TableCell
        sx={{
          width: '25%',
          pr: 0.5,
          display: isSubid3View ? 'none' : 'table-cell' // Hide in subid3 view
        }}
      >
        {!isSubid3View ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              gap: 0.5
            }}
          >
            {/* Account badge */}
            {(() => {
              const badge = extractAccountBadge(campaign.campaign_name);
              if (!badge) return null;
              return (
                <Tooltip title={badge.type ? `${badge.account} (${badge.type})` : badge.account}>
                  <Chip
                    size="small"
                    label={badge.account}
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      backgroundColor: badge.type === 'Widget' ? 'secondary.light' : 'primary.light',
                      color: badge.type === 'Widget' ? 'secondary.contrastText' : 'primary.contrastText',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                </Tooltip>
              );
            })()}
            {/* Domain badge - only show for non-child rows since child rows show domain as the name */}
            {!isChildRow && (() => {
              let domain = extractDomainIdentifier(campaign.campaign_name);
              // Default to thecozyguide if no domain found
              if (!domain || domain === 'unknown') {
                domain = 'thecozyguide';
              }

              // Assign different colors based on domain
              const domainColors = {
                'knowledgia': { bg: '#e3f2fd', color: '#1565c0' },        // Blue
                'thecozyguide': { bg: '#e8f5e9', color: '#2e7d32' },      // Green
                'waveshare': { bg: '#fff3e0', color: '#e65100' },         // Orange
                'default': { bg: '#f3e5f5', color: '#7b1fa2' }            // Purple
              };
              const colorScheme = domainColors[domain.toLowerCase()] || domainColors['default'];

              return (
                <Tooltip title={`Domain: ${domain}`}>
                  <Chip
                    size="small"
                    label={domain}
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 500,
                      backgroundColor: colorScheme.bg,
                      color: colorScheme.color,
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                </Tooltip>
              );
            })()}
            {/* Channel warning icon for knowledgia without channel or duplicate channels */}
            {(() => {
              if (!channelWarnings) return null;

              const domain = extractDomainIdentifier(campaign.campaign_name);
              const channelId = channelWarnings.campaign_channels?.[campaign.campaign_id];
              const isKnowledgia = domain?.toLowerCase() === 'knowledgia';
              const isDuplicate = channelId && channelWarnings.duplicate_channels?.[channelId];
              const missingChannel = isKnowledgia && !channelId;

              if (!missingChannel && !isDuplicate) return null;

              let warningMessage = '';
              if (missingChannel) {
                warningMessage = 'Knowledgia campaign missing channel ID';
              } else if (isDuplicate) {
                const otherCampaigns = channelWarnings.duplicate_channels[channelId].filter(id => id !== campaign.campaign_id);
                warningMessage = `Duplicate channel ID: ${channelId} (shared with ${otherCampaigns.length} other campaign${otherCampaigns.length > 1 ? 's' : ''})`;
              }

              return (
                <Tooltip title={warningMessage}>
                  <WarningIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: missingChannel ? '#ed6c02' : '#d32f2f',
                      ml: 0.5
                    }}
                  />
                </Tooltip>
              );
            })()}
            {/* No revenue warning when cost > $5 */}
            {(() => {
              const cost = parseFloat(campaign.cost || 0);
              const revenue = parseFloat(campaign.revenue || 0);

              if (cost <= 5 || revenue > 0) return null;

              return (
                <Tooltip title={`No revenue with $${cost.toFixed(2)} cost - check tracking`}>
                  <WarningIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: '#9c27b0',
                      ml: 0.5
                    }}
                  />
                </Tooltip>
              );
            })()}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
            <Tooltip title={campaign.campaign_name} enterDelay={500}>
              <span style={{ fontSize: '0.72rem' }}>
                {(() => {
                  // For child rows in a group, show the domain identifier for easier identification
                  if (isChildRow) {
                    const domain = extractDomainIdentifier(campaign.campaign_name);
                    return domain !== 'unknown' ? domain : cleanCampaignName(campaign.campaign_name);
                  }

                  // For Facebook campaigns, check if we can get the name from Facebook API data
                  if (campaign.platform === 'facebook' || isFacebookCampaign(campaign.campaign_id)) {
                    const fbCampaign = getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
                    if (fbCampaign) {
                      const nameToShow = fbCampaign.display_name || fbCampaign.name || campaign.campaign_name || '';
                      return cleanCampaignName(nameToShow);
                    }
                  }

                  // Fall back to the campaign_name from combined data
                  return cleanCampaignName(campaign.campaign_name);
                })()}
              </span>
            </Tooltip>
            </Box>
            <Tooltip title={`Copy ID: ${campaign.campaign_id}`}>
              <IconButton
                size="small"
                onClick={handleCopyId}
                sx={{
                  padding: '2px',
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  ml: 0.5,
                  flexShrink: 0,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: '0.85rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Tooltip title={campaign.campaign_name} enterDelay={500}>
              <span style={{ fontSize: '0.72rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(() => {
                  // For Facebook campaigns, check if we can get the name from Facebook API data
                  if (campaign.platform === 'facebook') {
                    const fbCampaign = getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
                    if (fbCampaign && fbCampaign.name) {
                      return cleanCampaignName(fbCampaign.name);
                    }
                  }

                  // Fall back to the campaign_name from combined data
                  return cleanCampaignName(campaign.campaign_name);
                })()}
              </span>
            </Tooltip>
            <Tooltip title={`Copy ID: ${campaign.campaign_id}`}>
              <IconButton
                size="small"
                onClick={handleCopyId}
                sx={{
                  padding: '2px',
                  width: 20,
                  height: 20,
                  ml: 0.5,
                  flexShrink: 0,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: '0.85rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </TableCell>

      {/* Campaign ID column - removed, copy button is next to name */}

      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Tooltip title="Bought Clicks: Clicks purchased from traffic source">
          <span>
            {(() => {
              if (campaign.platform === 'facebook') {
                // For Facebook campaigns, show Facebook ad clicks (bought clicks)
                return parseInt(campaign.clicks || 0, 10).toLocaleString();
              } else {
                // For other campaigns, we don't have separate bought clicks data
                return parseInt(campaign.clicks || 0, 10).toLocaleString();
              }
            })()}
          </span>
        </Tooltip>
      </TableCell>
      {/* Visits and Traffic Lost columns removed - always 0 */}
      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {/* Calculate CTR: Facebook Clicks / Facebook Impressions * 100 */}
        <Tooltip title="Click-Through Rate: Facebook clicks / Facebook impressions × 100">
          <span>
            {(() => {
              if (campaign.platform === 'facebook') {
                // For Facebook: clicks / impressions from Facebook data
                const fbImpressions = parseInt(campaign.impressions || 0, 10);
                const fbClicks = parseInt(campaign.clicks || 0, 10);
                return fbImpressions > 0
                  ? ((fbClicks / fbImpressions) * 100).toFixed(2) + '%'
                  : '0.00%';
              } else {
                // For non-Facebook campaigns, show N/A
                return 'N/A';
              }
            })()}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {/* Calculate CVR: Demand Clicks / Bought Clicks * 100 */}
        <Tooltip title="Conversion Rate: Demand clicks / Bought clicks (traffic source) × 100">
          <span>
            {(() => {
              // Get bought clicks (traffic source clicks) - use clicks field for all platforms
              const boughtClicks = parseInt(campaign.clicks || 0, 10);

              // Get demand clicks (conversions)
              const demandClicks = parseInt(campaign.demand_clicks || 0, 10);

              return boughtClicks > 0
                ? ((demandClicks / boughtClicks) * 100).toFixed(2) + '%'
                : '0.00%';
            })()}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {/* Conversions: Demand provider clicks */}
        <Tooltip title="Conversions: Clicks from demand provider (monetized clicks)">
          <span>
            {(() => {
              // Use demand_clicks for all campaigns (this is what generates revenue)
              const demandClicks = parseInt(campaign.demand_clicks || 0, 10);
              return demandClicks.toLocaleString();
            })()}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell">
        {campaign.cost !== undefined
          ? `$${parseFloat(campaign.cost || 0).toFixed(2)}`
          : '$0.00'}
      </TableCell>
      <TableCell align="right" className="number-cell">
        ${parseFloat(campaign.revenue || 0).toFixed(2)}
      </TableCell>
      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Tooltip title="Earnings Per Conversion: Revenue / Conversions (demand provider clicks)">
          <span>
            {(() => {
              const revenue = parseFloat(campaign.revenue || 0);
              const demandClicks = parseInt(campaign.demand_clicks || 0, 10);
              if (demandClicks > 0) {
                const epc = revenue / demandClicks;
                return `$${epc.toFixed(2)}`;
              }
              // Fallback to payout if available
              if (campaign.payout !== undefined) {
                return `$${parseFloat(campaign.payout || 0).toFixed(2)}`;
              }
              return '$0.00';
            })()}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Tooltip title="Cost Per Click: Cost divided by total clicks">
          <span>
            {(() => {
              const cost = parseFloat(campaign.cost || 0);
              const clicks = parseInt(campaign.clicks || 0, 10);
              if (clicks > 0) {
                const cpc = cost / clicks;
                return `$${cpc.toFixed(2)}`;
              }
              return '$0.00';
            })()}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell"
        sx={{
          color: parseFloat(campaign.roas || 0) >= 1 
            ? 'success.main' 
            : 'error.main'
        }}
      >
        <Tooltip title="Return on Ad Spend: Revenue / Cost. Value of 1.0 = breakeven, >1.0 is profitable">
          <span>
            {campaign.roas !== undefined 
              ? parseFloat(campaign.roas || 0).toFixed(2)
              : 'N/A'}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right" className="number-cell"
        sx={{
          color: parseFloat(campaign.profit || 0) >= 0
            ? 'success.main'
            : 'error.main',
          display: { xs: 'none', md: 'table-cell' }
        }}
      >
        {campaign.profit !== undefined
          ? `$${parseFloat(campaign.profit || 0).toFixed(2)}`
          : 'N/A'}
      </TableCell>
      <TableCell
        align="right"
        className="number-cell"
        sx={{
          display: isSubid3View ? 'none' : { xs: 'none', md: 'table-cell' }
        }}
        data-testid={`budget-cell-${campaign.campaign_id}`}
      >
        {(() => {
          // Check if it's a Facebook campaign
          const isFb = isFacebookCampaign(campaign.campaign_id);
          if (isFb || campaign.platform === 'facebook') {
            const fbCampaign = getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
            if (!fbCampaign) {
              return <Typography variant="caption" color="text.secondary">Loading...</Typography>;
            }
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <span className="number-cell">
                  {(() => {
                    // Get the budget value from direct Facebook campaign data
                    let budgetValue = fbCampaign.daily_budget;

                    // If daily_budget is not available, try other sources
                    if (budgetValue === undefined || budgetValue === null) {
                      if (fbCampaign.budget !== undefined && fbCampaign.budget !== null) {
                        budgetValue = fbCampaign.budget;
                      } else if (fbCampaign.lifetime_budget !== undefined && fbCampaign.lifetime_budget !== null) {
                        budgetValue = parseFloat(fbCampaign.lifetime_budget) / 30;
                      }
                    }

                    // Process the budget value
                    const displayBudget = budgetValue !== undefined && budgetValue !== null && !isNaN(parseFloat(budgetValue))
                      ? parseFloat(budgetValue)
                      : null;

                    const formattedBudget = displayBudget !== null
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(displayBudget)
                      : '-';

                    return formattedBudget;
                  })()}
                </span>
                <Tooltip title={`Edit Budget (Current: ${(() => {
                  let budgetValue = fbCampaign.daily_budget;
                  
                  // If daily_budget is not available, try other sources
                  if (budgetValue === undefined || budgetValue === null) {
                    if (fbCampaign.budget !== undefined && fbCampaign.budget !== null) {
                      budgetValue = fbCampaign.budget;
                    } else if (fbCampaign.lifetime_budget !== undefined && fbCampaign.lifetime_budget !== null) {
                      budgetValue = parseFloat(fbCampaign.lifetime_budget) / 30;
                    }
                  }
                  
                  // Process the budget value
                  const displayBudget = budgetValue !== undefined && budgetValue !== null && !isNaN(parseFloat(budgetValue))
                    ? parseFloat(budgetValue)
                    : null;
                    
                  const formattedBudget = displayBudget !== null 
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(displayBudget)
                    : '-';
                    
                  return formattedBudget;
                })()})`}>
                  <IconButton 
                    size="small"
                    sx={{ 
                      padding: '2px', 
                      ml: 0.5,
                      width: 24, 
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    color="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openBudgetDialog({
                        ...campaign, 
                        platform: 'facebook',
                        daily_budget: fbCampaign.daily_budget,
                        fbCampaign: fbCampaign
                      });
                    }}
                    disabled={loading}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          } else if (campaign.platform == 'taboola') {
            const taboolaCampaign = getTaboolaCampaignData(campaign.campaign_id);
            if (taboolaCampaign) {
              // Get the budget value from Taboola campaign
              const budgetValue = taboolaCampaign.daily_cap;
              const displayBudget = budgetValue !== undefined && budgetValue !== null && !isNaN(parseFloat(budgetValue)) && parseFloat(budgetValue) > 0
                ? parseFloat(budgetValue)
                : null;
                
              const formattedBudget = displayBudget !== null 
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(displayBudget)
                : '-';
                
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <span className="number-cell">
                    {formattedBudget}
                  </span>
                  <Tooltip title={`Edit Budget (Current: ${formattedBudget})`}>
                    <IconButton 
                      size="small"
                      sx={{ 
                        padding: '2px', 
                        ml: 0.5,
                        width: 24, 
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openBudgetDialog({
                          ...campaign, 
                          platform: 'taboola',
                          daily_cap: taboolaCampaign.daily_cap,
                          taboolaCampaign: taboolaCampaign
                        });
                      }}
                      disabled={loading}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            }
          }
          return '-';
        })()}
      </TableCell>
      {/* Blacklist button (only for subid3 view in Taboola campaigns) */}
      <TableCell sx={{ display: isSubid3View ? 'table-cell' : 'none' }}>
        {isSubid3View && isTaboolaCampaign(selectedSubid2 || campaign.subid2) && handleBlacklist && (
          <Tooltip title={isBlacklisted && isBlacklisted(campaign.campaign_id) ? 
            "Remove from Blacklist" : "Add to Blacklist"}>
            <IconButton 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBlacklist(e);
              }}
              color={isBlacklisted && isBlacklisted(campaign.campaign_id) ? "success" : "error"}
              size="small"
              disabled={blacklistLoading}
              sx={{ 
                width: 28, 
                height: 28, 
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {blacklistLoading ? (
                <CircularProgress size={14} color="inherit" thickness={4} sx={{ display: 'block' }} />
              ) : (
                isBlacklisted && isBlacklisted(campaign.campaign_id) ? 
                  <RemoveCircleOutlineIcon fontSize="small" /> : <BlockIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ display: isSubid3View ? 'none' : 'table-cell' }}>
        {(() => {
          // Check if it's a Facebook campaign
          const isFb = isFacebookCampaign(campaign.campaign_id);
          if (isFb || campaign.platform === 'facebook') {
            const fbCampaign = getFacebookCampaignData(campaign.campaign_id, null, facebookCampaigns);
            if (!fbCampaign) {
              return <Typography variant="caption" color="text.secondary">Loading...</Typography>;
            }
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Tooltip title={fbCampaign.is_active ? "Pause Campaign" : "Activate Campaign"}>
                  <IconButton 
                    size="small"
                    sx={{ 
                      padding: '2px',
                      bgcolor: fbCampaign.is_active ? 'rgba(239, 83, 80, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                      width: 28, 
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    color={fbCampaign.is_active ? "error" : "success"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Set loading state for this campaign
                      setStatusLoading(prev => ({...prev, [campaign.campaign_id]: true}));
                      
                      // Create a complete campaign object
                      const campaignObject = {
                        campaign_id: campaign.campaign_id,
                        is_active: fbCampaign.is_active,
                        platform: 'facebook',
                        name: fbCampaign.name || campaign.campaign_name,
                        fbCampaign: fbCampaign
                      };
                      
                      // Call the toggle function and clear loading state when done
                      toggleCampaignStatus(campaignObject)
                        .finally(() => {
                          setStatusLoading(prev => ({...prev, [campaign.campaign_id]: false}));
                        });
                    }}
                    disabled={loading || statusLoading[campaign.campaign_id]}
                  >
                    {statusLoading[campaign.campaign_id] ? 
                      <CircularProgress size={16} color="inherit" thickness={4} /> : 
                      (fbCampaign.is_active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />)
                    }
                  </IconButton>
                </Tooltip>
              </Box>
            );
          } else if (campaign.platform === 'taboola') {
            const taboolaCampaign = getTaboolaCampaignData(campaign.campaign_id);
            if (!taboolaCampaign) {
              return <Typography variant="caption" color="text.secondary">Loading...</Typography>;
            }
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Tooltip title={taboolaCampaign.is_active ? "Pause Campaign" : "Activate Campaign"}>
                  <IconButton 
                    size="small"
                    sx={{ 
                      padding: '2px',
                      bgcolor: taboolaCampaign.is_active ? 'rgba(239, 83, 80, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                      width: 28, 
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    color={taboolaCampaign.is_active ? "error" : "success"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Set loading state for this campaign
                      setStatusLoading(prev => ({...prev, [campaign.campaign_id]: true}));
                      
                      // Create a complete campaign object
                      const campaignObject = {
                        campaign_id: campaign.campaign_id,
                        is_active: taboolaCampaign.is_active,
                        platform: 'taboola',
                        name: campaign.campaign_name || taboolaCampaign.name,
                        taboolaCampaign: taboolaCampaign
                      };
                      
                      // Call the toggle function and clear loading state when done
                      toggleCampaignStatus(campaignObject)
                        .finally(() => {
                          setStatusLoading(prev => ({...prev, [campaign.campaign_id]: false}));
                        });
                    }}
                    disabled={loading || statusLoading[campaign.campaign_id]}
                  >
                    {statusLoading[campaign.campaign_id] ? 
                      <CircularProgress size={16} color="inherit" thickness={4} /> : 
                      (taboolaCampaign.is_active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />)
                    }
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }
          return <Typography variant="caption" color="text.secondary">Not manageable</Typography>;
        })()}
      </TableCell>
    </TableRow>
  );
};

export default CampaignTableRow; 